/**
 * Value helpers for `guilds.metadata` — the JSONB bag the Metadata card edits
 * and the save-review dialog reports on.
 *
 * They live apart from both components because the two must agree exactly: the
 * dialog claims "this is what is going up", and it can only be true if it reads
 * the same object the editor emits with the same rules for what a value is.
 */

/** What a metadata value is, as the card's type tag says it. */
export type MetadataValueKind =
  | "int"
  | "number"
  | "string"
  | "bool"
  | "object"
  | "array"
  | "null";

/** The kind of a raw metadata value, inferred from the value itself. */
export function valueKind(v: unknown): MetadataValueKind {
  if (v === null) return "null";
  if (Array.isArray(v)) return "array";
  switch (typeof v) {
    case "boolean":
      return "bool";
    case "number":
      return Number.isInteger(v) ? "int" : "number";
    case "string":
      return "string";
    default:
      return "object";
  }
}

/**
 * True for a value the form can edit in place. Objects, arrays and `null` are
 * shown read-only and edited in Raw JSON — a text box is not an editor for
 * them, and pretending otherwise is how a nested object becomes the string
 * "[object Object]".
 */
export function isEditableScalar(v: unknown): boolean {
  const k = valueKind(v);
  return k === "int" || k === "number" || k === "string" || k === "bool";
}

/**
 * One-line JSON, spaced the way the mockup prints a nested value:
 * `{ "double_xp": true, "banner": "wolf" }`. JSON.stringify's own compact form
 * has no spaces and its indented form spans lines; this is the middle one, and
 * it is what both the read-only rows and the diff list show.
 */
export function compactJson(v: unknown): string {
  if (v === undefined) return "";
  if (Array.isArray(v)) {
    if (v.length === 0) return "[]";
    return `[ ${v.map(compactJson).join(", ")} ]`;
  }
  if (v !== null && typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>);
    if (entries.length === 0) return "{}";
    const inner = entries
      .map(([k, val]) => `${JSON.stringify(k)}: ${compactJson(val)}`)
      .join(", ");
    return `{ ${inner} }`;
  }
  return JSON.stringify(v) ?? "null";
}

/**
 * Deep JSON-equality for two metadata bags, independent of key order — the
 * form emits its keys in schema order, which is never the order the column
 * came back in, and a re-ordered object is not an edit.
 */
export function metadataEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const ak = Object.keys(a).sort();
  const bk = Object.keys(b).sort();
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    if (ak[i] !== bk[i]) return false;
    if (!sameJsonValue(a[ak[i]], b[bk[i]])) return false;
  }
  return true;
}

/** Value equality for two JSON values, again ignoring key order. */
export function sameJsonValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((v, i) => sameJsonValue(v, b[i]));
  }
  if (
    a !== null &&
    b !== null &&
    typeof a === "object" &&
    typeof b === "object"
  ) {
    return metadataEqual(
      a as Record<string, unknown>,
      b as Record<string, unknown>,
    );
  }
  return false;
}

// === The save-review diff ============================================= //

/** What happened to one key between the loaded object and the edited one. */
export type MetadataChangeKind = "added" | "changed" | "removed";

/** One line of the review dialog's list. */
export interface MetadataDiffRow {
  key: string;
  kind: MetadataChangeKind;
  /** Rendered previous value — absent for `added`. */
  before?: string;
  /** Rendered next value — absent for `removed`. */
  after?: string;
  /** The project's `guild.metadata_schema` does not declare this key. */
  outsideSchema: boolean;
}

export interface MetadataDiff {
  rows: MetadataDiffRow[];
  /** Keys that go up untouched — the PATCH still carries them. */
  unchanged: number;
}

const KIND_ORDER: Record<MetadataChangeKind, number> = {
  added: 0,
  changed: 1,
  removed: 2,
};

/**
 * Compare the loaded metadata with what the editor would send.
 *
 * Rows come out grouped added → changed → removed, and within a group the keys
 * the schema declares come first, in schema order: the reader recognises their
 * own project's fields before the ones the game invented.
 */
export function diffMetadata(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  schemaKeys: string[],
): MetadataDiff {
  const rows: MetadataDiffRow[] = [];
  let unchanged = 0;

  const keys: string[] = [];
  for (const k of Object.keys(before)) keys.push(k);
  for (const k of Object.keys(after)) if (!keys.includes(k)) keys.push(k);

  for (const key of keys) {
    const inBefore = Object.hasOwn(before, key);
    const inAfter = Object.hasOwn(after, key);
    const outsideSchema = !schemaKeys.includes(key);
    if (inBefore && inAfter) {
      if (sameJsonValue(before[key], after[key])) {
        unchanged++;
        continue;
      }
      rows.push({
        key,
        kind: "changed",
        before: compactJson(before[key]),
        after: compactJson(after[key]),
        outsideSchema,
      });
    } else if (inAfter) {
      rows.push({
        key,
        kind: "added",
        after: compactJson(after[key]),
        outsideSchema,
      });
    } else {
      rows.push({
        key,
        kind: "removed",
        before: compactJson(before[key]),
        outsideSchema,
      });
    }
  }

  const rank = (key: string) => {
    const i = schemaKeys.indexOf(key);
    return i === -1 ? schemaKeys.length : i;
  };
  rows.sort((a, b) => {
    const byKind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    if (byKind !== 0) return byKind;
    return rank(a.key) - rank(b.key);
  });

  return { rows, unchanged };
}
